import { db } from '../firebase';
import { doc, getDoc, setDoc, collection, getDocs, query, orderBy, limit, startAfter, DocumentSnapshot } from 'firebase/firestore';
import { DEFAULT_WORD_CIRCLES } from './wordService';

interface DailyPuzzle {
  date: string; // YYYY-MM-DD format
  words: string; // comma-delimited word list
  createdAt: Date;
}

// Collection name for daily puzzles
const PUZZLES_COLLECTION = 'daily_puzzles';

// Cache for daily puzzle to avoid multiple Firestore queries
let dailyPuzzleCache: { [date: string]: string[] } = {};
let lastFetchDate: string | null = null;

// Get today's date in YYYY-MM-DD format
function getTodayKey(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = (today.getMonth() + 1).toString().padStart(2, '0');
  const day = today.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Get date key for any date
function getDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Reset cache at midnight
function shouldResetCache(): boolean {
  const today = getTodayKey();
  return lastFetchDate !== today;
}

// Get a deterministic default puzzle based on date
function getDeterministicPuzzle(dateKey: string): string[] {
  const dateHash = dateKey.split('-').reduce((sum, part) => sum + parseInt(part), 0);
  const circleIndex = dateHash % DEFAULT_WORD_CIRCLES.length;
  return DEFAULT_WORD_CIRCLES[circleIndex];
}

// Fetch puzzle from Firestore for a specific date
async function fetchPuzzleFromFirestore(dateKey: string): Promise<string[] | null> {
  try {
    
    // Query the collection for the specific date
    const puzzleDoc = doc(db, PUZZLES_COLLECTION, dateKey);
    const docSnap = await getDoc(puzzleDoc);
    
    if (docSnap.exists()) {
      const data = docSnap.data() as DailyPuzzle;
      const words = data.words.split(',').map(word => word.trim()).filter(word => word.length > 0);
      return words;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
}

// Save puzzle to Firestore
async function savePuzzleToFirestore(dateKey: string, words: string[]): Promise<boolean> {
  try {
    
    const puzzleData: DailyPuzzle = {
      date: dateKey,
      words: words.join(', '),
      createdAt: new Date(),
    };
    
    await setDoc(doc(db, PUZZLES_COLLECTION, dateKey), puzzleData);
    return true;
  } catch (error) {
    return false;
  }
}

// Get cached puzzle from localStorage
function getCachedPuzzle(dateKey: string): string[] | null {
  try {
    const cacheKey = `pathways-daily-puzzle-${dateKey}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const words = JSON.parse(cached);
      return words;
    }
  } catch (error) {
  }
  return null;
}

// Cache puzzle in localStorage
function cachePuzzle(dateKey: string, words: string[]): void {
  try {
    const cacheKey = `pathways-daily-puzzle-${dateKey}`;
    localStorage.setItem(cacheKey, JSON.stringify(words));
    
    // Clean up old cache entries (keep only last 7 days)
    const today = new Date();
    for (let i = 7; i < 30; i++) {
      const oldDate = new Date(today);
      oldDate.setDate(oldDate.getDate() - i);
      const oldKey = `pathways-daily-puzzle-${getDateKey(oldDate)}`;
      localStorage.removeItem(oldKey);
    }
  } catch (error) {
  }
}

// Main function to get today's puzzle
export async function getTodaysPuzzle(): Promise<string[]> {
  const dateKey = getTodayKey();
  
  // Reset cache if it's a new day
  if (shouldResetCache()) {
    dailyPuzzleCache = {};
    lastFetchDate = dateKey;
  }
  
  // Check memory cache first
  if (dailyPuzzleCache[dateKey]) {
    return dailyPuzzleCache[dateKey];
  }
  
  // Check localStorage cache
  const cachedPuzzle = getCachedPuzzle(dateKey);
  if (cachedPuzzle) {
    dailyPuzzleCache[dateKey] = cachedPuzzle;
    return cachedPuzzle;
  }
  
  try {
    // Try to fetch from Firestore
    const firestorePuzzle = await fetchPuzzleFromFirestore(dateKey);
    
    if (firestorePuzzle) {
      // Cache the puzzle
      dailyPuzzleCache[dateKey] = firestorePuzzle;
      cachePuzzle(dateKey, firestorePuzzle);
      return firestorePuzzle;
    } else {
      // No Firestore puzzle - use deterministic fallback
      const fallbackPuzzle = getDeterministicPuzzle(dateKey);
      
      // Cache the fallback puzzle locally (but don't save to Firestore)
      dailyPuzzleCache[dateKey] = fallbackPuzzle;
      cachePuzzle(dateKey, fallbackPuzzle);
      return fallbackPuzzle;
    }
  } catch (error) {
    
    // Ultimate fallback
    const fallbackPuzzle = getDeterministicPuzzle(dateKey);
    dailyPuzzleCache[dateKey] = fallbackPuzzle;
    return fallbackPuzzle;
  }
}

// Admin function to seed Firestore with example puzzles
export async function seedFirestoreWithPuzzles(daysToSeed: number = 7): Promise<boolean> {
  try {
    
    const today = new Date();
    const promises: Promise<boolean>[] = [];
    
    for (let i = 0; i < daysToSeed; i++) {
      const targetDate = new Date(today);
      targetDate.setDate(targetDate.getDate() + i);
      const dateKey = getDateKey(targetDate);
      
      // Use a different default puzzle for each day
      const puzzleIndex = i % DEFAULT_WORD_CIRCLES.length;
      const words = DEFAULT_WORD_CIRCLES[puzzleIndex];
      
      promises.push(savePuzzleToFirestore(dateKey, words));
    }
    
    const results = await Promise.all(promises);
    const successCount = results.filter(Boolean).length;
    
    return successCount === daysToSeed;
  } catch (error) {
    return false;
  }
}

// Function to check if a puzzle exists for today in Firestore
export async function hasTodaysPuzzleInFirestore(): Promise<boolean> {
  const dateKey = getTodayKey();
  try {
    const puzzleDoc = doc(db, PUZZLES_COLLECTION, dateKey);
    const docSnap = await getDoc(puzzleDoc);
    return docSnap.exists();
  } catch (error) {
    return false;
  }
}

// Interface for paginated puzzle results
export interface PaginatedPuzzles {
  puzzles: DailyPuzzle[];
  hasMore: boolean;
  lastDoc: DocumentSnapshot | null;
}

// Function to get paginated puzzles (for admin purposes)
export async function getPaginatedPuzzles(
  pageSize: number = 25,
  lastDocument: DocumentSnapshot | null = null
): Promise<PaginatedPuzzles> {
  try {
    
    const puzzlesRef = collection(db, PUZZLES_COLLECTION);
    let puzzleQuery = query(
      puzzlesRef,
      orderBy('date', 'desc'), // Most recent first (including future dates)
      limit(pageSize + 1) // Fetch one extra to check if there are more
    );
    
    if (lastDocument) {
      puzzleQuery = query(
        puzzlesRef,
        orderBy('date', 'desc'),
        startAfter(lastDocument),
        limit(pageSize + 1)
      );
    }
    
    const snapshot = await getDocs(puzzleQuery);
    const puzzles: DailyPuzzle[] = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      puzzles.push({
        date: doc.id,
        words: data.words,
        createdAt: data.createdAt?.toDate() || new Date()
      });
    });
    
    // Check if there are more results
    const hasMore = puzzles.length > pageSize;
    if (hasMore) {
      puzzles.pop(); // Remove the extra item
    }
    
    const lastDoc = puzzles.length > 0 ? 
      snapshot.docs[puzzles.length - 1] : null;
    
    
    return {
      puzzles,
      hasMore,
      lastDoc
    };
  } catch (error) {
    return {
      puzzles: [],
      hasMore: false,
      lastDoc: null
    };
  }
}

// Function to get all available puzzle dates from Firestore (for admin purposes)
// DEPRECATED: Use getPaginatedPuzzles instead for better performance
export async function getAvailablePuzzleDates(): Promise<string[]> {
  try {
    const puzzlesRef = collection(db, PUZZLES_COLLECTION);
    const snapshot = await getDocs(puzzlesRef);
    
    const dates: string[] = [];
    snapshot.forEach((doc) => {
      dates.push(doc.id);
    });
    
    return dates.sort().reverse(); // Most recent first
  } catch (error) {
    return [];
  }
}